// The chrome shared by both threshold sliders (ACT2-534): handle labels, the plot frame, the axis
// track with its thumbs and landmark ticks, and the axis scale row.
//
// The PLOT BODY is `children`. The shell positions nothing inside it and assumes nothing about its
// shape — bars today, bands today, a density curve later — it only supplies the frame and the value→
// percent mapping (via `slider.pct`) that every shape must be drawn against. Swapping a body out is a
// change to one caller, not to this file.

import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useContainerWidth } from '@/_shared/hooks/use-container-width';
import { cn } from '@/_shared/lib/utils';

import type { SliderHandle, ThresholdSlider } from './hooks/useThresholdSlider';
import type { AxisEnd, AxisLabel } from './lib/axis-labels';
import { placeAxisLabels } from './lib/axis-labels';
import { PLOT_HEIGHT } from './lib/slider-geometry';
import type { Span } from './lib/slider-ops';

export type AxisLandmark = AxisLabel;
export type { AxisEnd };

// Scale-row metrics, expressed as multiples of the row's OWN font size rather than as pixels. The face is
// monospaced, so character count times advance IS the width — but `0.625rem` is only 10px while the root
// font is 16px. A reader on a larger default gets wider text against a width still measured in real
// pixels, so fixed px advances under-measure every label and the algorithm clears collisions that then
// happen anyway.
const CHAR_ADVANCE = 0.6; // monospace advance, in em
const LABEL_SCALE = 0.8; // the name line is 0.5rem against the value line's 0.625rem
const GAP_EM = 0.8; // clear space between two neighbouring labels
/** Row font size assumed until it can be read — `0.625rem` at a 16px root. */
const ASSUMED_FONT_PX = 10;
/** Width assumed until the row reports its own: the insight pane is `max-w-150` (600px) less its `p-3`
 *  padding and border. Without a stand-in the first paint places nothing and the labels pop in a frame
 *  later, which is the flicker this constant exists to prevent — so it has to be close to the truth. */
const ASSUMED_SCALE_WIDTH = 574;

/** The element's rendered font size in px. Re-read whenever the row resizes, since a root-font change
 *  moves both. */
function useFontPx(ref: RefObject<HTMLElement | null>, width: number): number {
  const [px, setPx] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const size = Number.parseFloat(getComputedStyle(el).fontSize);
    if (Number.isFinite(size) && size > 0) setPx(size);
  }, [ref, width]);
  return px;
}

export function SliderShell({
  slider,
  summary,
  children,
  ticks = [],
  landmarks = [],
  minLabel,
  maxLabel,
  segments: segmentsOverride,
  softSegments = [],
  guideStyle = 'dashed',
  caption,
}: {
  slider: ThresholdSlider;
  summary: ReactNode;
  /** The plot body — value-positioned shapes, drawn against `slider.pct`. */
  children: ReactNode;
  /** Landmark tick positions on the axis, in percent. */
  ticks?: number[];
  landmarks?: AxisLandmark[];
  /** The axis ends: each a value, plus the name of what that end is when it can be proven. */
  minLabel: AxisEnd;
  maxLabel: AxisEnd;
  /** Definite-match spans, when the caller derives them itself (eq/neq); defaults to the slider's own. */
  segments?: Span[];
  /** Spans that only CONTAIN the match rather than equalling it — drawn muted, never as a definite hit. */
  softSegments?: Span[];
  /** `solid` marks an exact value the chart cannot resolve (eq/neq); `dashed` is an ordinary threshold. */
  guideStyle?: 'dashed' | 'solid';
  caption?: string;
}) {
  const { committed, disabled, handles, pointerProps, wrapRef } = slider;
  const segments = segmentsOverride ?? slider.segments;
  // Placement lives here, not in the callers: both sliders want the same rules, and it needs the scale
  // row's own measured width — which only this component has. A dropped label keeps its tick either way.
  const scaleRef = useRef<HTMLDivElement>(null);
  const scaleWidth = useContainerWidth(scaleRef) || ASSUMED_SCALE_WIDTH;
  const fontPx = useFontPx(scaleRef, scaleWidth) || ASSUMED_FONT_PX;
  const placed = placeAxisLabels(
    landmarks,
    { min: minLabel, max: maxLabel },
    {
      width: scaleWidth,
      valueChar: fontPx * CHAR_ADVANCE,
      labelChar: fontPx * LABEL_SCALE * CHAR_ADVANCE,
      gap: fontPx * GAP_EM,
    },
  );
  const inAnySpan = (p: number, spans: Span[]) => spans.some(([from, to]) => p >= from - 0.5 && p <= to + 0.5);

  return (
    <div className="flex flex-col gap-2">
      {summary}
      <div
        ref={wrapRef}
        className={cn(
          'relative touch-none pt-5 select-none',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-ew-resize',
        )}
        {...pointerProps}
      >
        {handles.map((h, i) => (
          <span
            key={`label-${i}`}
            className={cn(
              'pointer-events-none absolute top-0 -translate-x-1/2 rounded px-1 py-0.5 font-mono text-[0.625rem] font-semibold whitespace-nowrap',
              committed ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
            )}
            style={{ left: `${h.pos}%` }}
          >
            {h.label}
          </span>
        ))}

        <div className={cn('relative', PLOT_HEIGHT)}>
          {children}
          {handles.map((h, i) => (
            <div
              key={`guide-${i}`}
              className={cn(
                'pointer-events-none absolute inset-y-0 -translate-x-1/2',
                guideStyle === 'solid' ? 'border-l-2' : 'border-l border-dashed',
                committed
                  ? guideStyle === 'solid'
                    ? 'border-primary'
                    : 'border-primary/50'
                  : 'border-muted-foreground/30',
              )}
              style={{ left: `${h.pos}%` }}
            />
          ))}
        </div>

        {/* The axis line — the track — carrying the highlighted span(s), the landmark ticks and the thumbs. */}
        <div className="relative mt-0.5 h-3.5">
          <div className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-border" />
          {committed &&
            softSegments.map(([from, to], i) => (
              <div
                key={`soft-${i}`}
                className="absolute top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-primary/30"
                style={{ left: `${from}%`, width: `${Math.max(0, to - from)}%` }}
              />
            ))}
          {committed &&
            segments.map(([from, to], i) => (
              <div
                key={`seg-${i}`}
                className="absolute top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-primary"
                style={{ left: `${from}%`, width: `${Math.max(0, to - from)}%` }}
              />
            ))}
          {/* Ticks render ON TOP of the selected fill so they stay visible; a tick inside the highlight
              flips to the primary-foreground colour to contrast against the accent. */}
          {ticks.map((p, i) => (
            <div
              key={`tick-${i}`}
              className={cn(
                'pointer-events-none absolute top-1/2 h-1.5 w-px -translate-x-1/2 -translate-y-1/2',
                committed && inAnySpan(p, segments) ? 'bg-primary-foreground/80' : 'bg-muted-foreground/50',
              )}
              style={{ left: `${p}%` }}
              aria-hidden
            />
          ))}
          {handles.map((h, i) => (
            <Thumb key={`thumb-${i}`} handle={h} slider={slider} />
          ))}
        </div>
      </div>

      {/* ONE scale row: the data's start and end anchor the edges, and every landmark that fits sits
          between them, each showing its VALUE over its name and centred on the position it marks. What
          made the old form ambiguous was the name sitting inline and to the LEFT of its value ("median
          Aug 28, 1970"), which put the word a good 40px from the position it named and let it read
          against a neighbour's number (ACT2-534). Which landmarks make the cut is decided by
          `placeAxisLabels` against this row's measured width. */}
      <div ref={scaleRef} className="relative h-6 font-mono text-[0.625rem] text-muted-foreground">
        <span className="absolute top-0 left-0 flex flex-col items-start leading-tight">
          <span>{minLabel.value}</span>
          {minLabel.name && <span className="text-[0.5rem] text-muted-foreground/60">{minLabel.name}</span>}
        </span>
        <span className="absolute top-0 right-0 flex flex-col items-end leading-tight">
          <span>{maxLabel.value}</span>
          {maxLabel.name && <span className="text-[0.5rem] text-muted-foreground/60">{maxLabel.name}</span>}
        </span>
        {placed.map((mark, i) => (
          <span
            key={`landmark-${i}`}
            className="pointer-events-none absolute top-0 flex -translate-x-1/2 flex-col items-center leading-tight whitespace-nowrap"
            style={{ left: `${mark.pos}%` }}
          >
            <span>{mark.value}</span>
            <span className="text-[0.5rem] text-muted-foreground/60">{mark.label}</span>
          </span>
        ))}
      </div>
      {caption && <p className="text-[0.6875rem] text-muted-foreground italic">{caption}</p>}
    </div>
  );
}

/**
 * The plot body redrawn once per span, each copy clipped to that span.
 *
 * This is how a boundary bar gets coloured only as far as the handle, so the highlighted AREA equals the
 * quoted count instead of merely approximating it. Shared rather than written per body: it was three
 * copies of the same `inset()` across the two sliders, and a fix to the clip maths would have landed on
 * whichever one the next bug was reported against.
 */
export function SpanFill({ spans, children }: { spans: Span[]; children: ReactNode }) {
  return spans.map(([from, to], i) => (
    <div
      key={i}
      className="pointer-events-none absolute inset-0"
      style={{ clipPath: `inset(0 ${100 - to}% 0 ${from}%)` }}
    >
      {children}
    </div>
  ));
}

/** A focusable slider thumb on the axis track — pointer drag is handled by the plot wrapper, so this is
 *  `pointer-events-none` but keyboard-operable (arrows step, shift ×10, Home/End jump). Greyed + dashed
 *  when the value is unset (the row's input was cleared). */
function Thumb({ handle, slider }: { handle: SliderHandle; slider: ThresholdSlider }) {
  const { clampValue, committed, disabled, max, min, stepAt } = slider;
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const big = e.shiftKey ? 10 : 1;
    let next: number;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = handle.value + stepAt(handle.value) * big;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = handle.value - stepAt(handle.value) * big;
        break;
      case 'Home':
        next = min;
        break;
      case 'End':
        next = max;
        break;
      default:
        return;
    }
    e.preventDefault();
    // The hook's own clamp, not a second copy of it — keyboard and drag must not be able to drift apart
    // on clamping policy.
    handle.onKey(clampValue(next));
  };

  return (
    <div
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={handle.ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={handle.value}
      aria-valuetext={handle.label}
      aria-disabled={disabled || undefined}
      onKeyDown={onKeyDown}
      className={cn(
        'pointer-events-none absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-card focus-ring',
        committed ? 'border-primary' : 'border-dashed border-muted-foreground',
      )}
      style={{ left: `${handle.pos}%` }}
    />
  );
}
